import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { roleIsLocked, RoleSummary, RoleSummaryStore } from 'src/stores/roles-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { useIdentity } from 'src/core/services/authentication';
import { ENV } from 'src/';
import { emptyTableModel, getRoleDisplayName } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { Selector, SelectorValue } from 'src/common/types';
import { guid } from 'odata-query';

const PartnerRolesListPage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const organizationId = identity.profile['tenant_id'];
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
    filter: { OrganizationId: guid(organizationId) },
  } as Query);

  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;

  setHeader(t('PartnerRoles'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'partnerroles', breadcrumbName: t('PartnerRoles') },
  ]);

  useEffect(
    () => {
      rolesStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onDelete = async (item: RoleSummary) => {
    await rolesStore.delete(item.id);
    await rolesStore.load(query);
  };

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={rolesStore.baseUrl}
            fieldName={'Name'}
            fieldValue={'name'}
            filter={`organizationId eq ${organizationId}`}
          />
        ),
        fiterField: 'Name',
        renderer: (data: RoleSummary) => <span>{getRoleDisplayName(data.name)}</span>,
      },
      {
        sortable: true,
        field: 'selector',
        title: t('Selector'),
        filter: (
          <OptionsFilter
            options={Object.entries(Selector)
              .filter(([_, value]) => SelectorValue[value] <= SelectorValue[Selector.Partners])
              .map(([key, value]) => ({ label: t(key), value: value }))}
          />
        ),
        renderer: (data: RoleSummary) => (
          <span>
            <Tag>{t(data.selector)}</Tag>
          </span>
        ),
      },
      {
        renderer: (data: RoleSummary) =>
          !roleIsLocked(data.name) && (
            <>
              <span className="clickable-span strong" onClick={() => navigate(`update/${data.id}`, { state: data })}>
                {t('Edit')}
              </span>
              {data.addToScopeTypeCreator == null && (
                <>
                  <span style={{ color: '#E1E1E1' }}>{' | '}</span>{' '}
                  <span className="clickable-span strong" onClick={() => onDelete(data)}>
                    {t('Delete')}
                  </span>
                </>
              )}
            </>
          ),
      },
    ],
    data: rolesState.value,
  } as TableModel<RoleSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew
          onNewItem={() => navigate(`new`)}
          newItemName={t('CreateRole')}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            rolesStore.load(query);
          }}
          onRefresh={() => rolesStore.load(query)}
          model={rolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            rolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(rolesState.errorMessage.value) : t('PartnerRolesTableError'))
          }
        />
      </Card>
    </Row>
  );
};

export default PartnerRolesListPage;
